import React, { useEffect, useState } from 'react'
import { useExpense } from '../contexts/ExpenseContext'
import { useAuth } from '../contexts/AuthContext'
import { budgetDashboardService } from '../lib/api'
import { 
  IndianRupee, 
  Receipt, 
  Target, 
  TrendingUp, 
  Calendar,
  Plus,
  ArrowUpRight,
  ArrowDownRight,
  Users
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts'
import LoadingSpinner from '../components/LoadingSpinner'
import { formatRupees } from '../utils/currency'

const DashboardPage = () => {
  const { user } = useAuth()
  const { expenses, budgets, stats, loading, loadExpenses, loadBudgets } = useExpense()
  const [sharedBudgets, setSharedBudgets] = useState([])
  const [sharedBudgetsLoading, setSharedBudgetsLoading] = useState(false)

  useEffect(() => {
    if (user) {
      loadExpenses()
      loadBudgets()
      loadSharedBudgets()
    }
  }, [user])

  const loadSharedBudgets = async () => {
    try {
      setSharedBudgetsLoading(true)
      const shared = await budgetDashboardService.getSharedBudgets()
      setSharedBudgets(shared)
    } catch (error) {
      console.error('Error loading shared budgets:', error)
    } finally {
      setSharedBudgetsLoading(false)
    }
  }

  // Recent expenses (last 5)
  const recentExpenses = expenses.slice(0, 5)

  // Prepare chart data
  const categoryData = Object.entries(stats.categoryBreakdown).map(([category, amount]) => ({
    name: category,
    value: amount
  }))

  // Colors for pie chart
  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#F97316', '#06B6D4']

  // Calculate real monthly data from expenses
  const getMonthlyData = () => {
    const monthlyExpenses = {}
    const now = new Date()
    
    // Initialize last 6 months with zero
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1)
      const key = date.toISOString().substr(0, 7) // YYYY-MM format
      const displayName = date.toLocaleDateString('en-US', { 
        month: 'short', 
        year: '2-digit' 
      })
      monthlyExpenses[key] = { amount: 0, displayName }
    }
    
    // Add actual expenses
    expenses.forEach(expense => {
      const expenseDate = new Date(expense.created_at)
      const monthKey = expenseDate.toISOString().substr(0, 7)
      
      if (monthlyExpenses[monthKey]) {
        monthlyExpenses[monthKey].amount += parseFloat(expense.amount)
      }
    })
    
    // Convert to sorted array
    return Object.entries(monthlyExpenses)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, data]) => ({
        month: data.displayName,
        amount: Math.round(data.amount * 100) / 100 // Round to 2 decimals
      }))
  }

  const monthlyData = getMonthlyData()

  // Calculate budget progress
  const getBudgetProgress = () => {
    return budgets.map(budget => {
      const spent = Object.entries(stats.categoryBreakdown).find(
        ([category]) => category === budget.category
      )?.[1] || 0
      
      const percentage = budget.amount > 0 ? (spent / budget.amount) * 100 : 0
      
      return {
        ...budget,
        spent,
        percentage: Math.min(percentage, 100),
        remaining: Math.max(budget.amount - spent, 0)
      }
    })
  }

  const budgetProgress = getBudgetProgress()

  if (loading) {
    return <LoadingSpinner message="Loading dashboard..." />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Dashboard
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Welcome back, {user?.email?.split('@')[0]}
          </p>
        </div>
        <Link
          to="/expenses"
          className="btn-primary flex items-center space-x-2"
        >
          <Plus className="h-4 w-4" />
          <span>Add Expense</span>
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Total Expenses
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatRupees(stats.total)}
              </p>
            </div>
            <IndianRupee className="h-8 w-8 text-primary-600" />
          </div>
          <div className="mt-2 flex items-center text-sm">
            <ArrowUpRight className="h-4 w-4 text-red-500 mr-1" />
            <span className="text-red-600">+12% from last month</span>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Total Transactions
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats.count}
              </p>
            </div>
            <Receipt className="h-8 w-8 text-green-600" />
          </div>
          <div className="mt-2 flex items-center text-sm">
            <ArrowUpRight className="h-4 w-4 text-green-500 mr-1" />
            <span className="text-green-600">+8% from last month</span>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Active Budgets
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {budgets.length}
              </p>
            </div>
            <Target className="h-8 w-8 text-yellow-600" />
          </div>
          <div className="mt-2 flex items-center text-sm">
            <ArrowDownRight className="h-4 w-4 text-green-500 mr-1" />
            <span className="text-green-600">-2 from last month</span>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Avg per Day
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatRupees(stats.total / 30)}
              </p>
            </div>
            <TrendingUp className="h-8 w-8 text-purple-600" />
          </div>
          <div className="mt-2 flex items-center text-sm">
            <ArrowUpRight className="h-4 w-4 text-red-500 mr-1" />
            <span className="text-red-600">+5% from last month</span>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Category Breakdown */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Expenses by Category
          </h3>
          {categoryData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={categoryData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={120}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {categoryData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [formatRupees(value), 'Amount']} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
              No expense data available
            </div>
          )}
          
          {/* Legend */}
          {categoryData.length > 0 && (
            <div className="mt-4 space-y-2">
              {categoryData.map((entry, index) => (
                <div key={entry.name} className="flex items-center justify-between text-sm">
                  <div className="flex items-center">
                    <div 
                      className="w-3 h-3 rounded-full mr-2"
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    ></div>
                    <span className="text-gray-700 dark:text-gray-300">{entry.name}</span>
                  </div>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {formatRupees(entry.value)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Monthly Trend */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Monthly Spending Trend
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip formatter={(value) => [formatRupees(value), 'Amount']} />
              <Bar dataKey="amount" fill="#3B82F6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent Activity & Budget Progress */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Expenses */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Recent Expenses
            </h3>
            <Link
              to="/expenses"
              className="text-primary-600 hover:text-primary-700 text-sm font-medium"
            >
              View all
            </Link>
          </div>
          
          {recentExpenses.length > 0 ? (
            <div className="space-y-3">
              {recentExpenses.map((expense) => (
                <div key={expense.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {expense.title}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {expense.category} • {new Date(expense.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900 dark:text-white">
                      {formatRupees(expense.amount)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <Receipt className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No expenses yet</p>
              <Link to="/expenses" className="text-primary-600 hover:text-primary-700 text-sm">
                Add your first expense
              </Link>
            </div>
          )}
        </div>

        {/* Budgets Shared With Me Section */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Budgets Shared With Me
            </h3>
            <Link
              to="/budget-dashboard"
              className="text-primary-600 hover:text-primary-700 text-sm font-medium"
            >
              View dashboard
            </Link>
          </div>
          
          {sharedBudgetsLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : sharedBudgets.length > 0 ? (
            <div className="space-y-3">
              {sharedBudgets.slice(0, 3).map((budget, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center text-white font-bold">
                      {budget.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {budget.name}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Budget: {formatRupees(budget.budget)}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900 dark:text-white">
                      {formatRupees(budget.left)} left
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Spent: {formatRupees(budget.spent)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No users have shared their budget with you yet</p>
              <Link to="/budget-dashboard" className="text-primary-600 hover:text-primary-700 text-sm">
                Go to budget dashboard
              </Link>
            </div>
          )}
        </div>

        {/* Budget Progress */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Budget Progress
            </h3>
            <Link
              to="/budgets"
              className="text-primary-600 hover:text-primary-700 text-sm font-medium"
            >
              Manage budgets
            </Link>
          </div>
          
          {budgetProgress.length > 0 ? (
            <div className="space-y-4">
              {budgetProgress.slice(0, 4).map((budget) => (
                <div key={budget.id} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {budget.category}
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {formatRupees(budget.spent)} / {formatRupees(budget.amount)}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${
                        budget.percentage > 90 
                          ? 'bg-red-500' 
                          : budget.percentage > 70 
                          ? 'bg-yellow-500' 
                          : 'bg-green-500'
                      }`}
                      style={{ width: `${budget.percentage}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400">
                    <span>{budget.percentage.toFixed(1)}% used</span>
                    <span>{formatRupees(budget.remaining)} remaining</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <Target className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No budgets set</p>
              <Link to="/budgets" className="text-primary-600 hover:text-primary-700 text-sm">
                Create your first budget
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default DashboardPage
